import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Shield,
  Upload,
  FileText,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Sparkles,
  Brain,
  Search,
  Download,
  ExternalLink,
  Info,
  WifiOff,
  Settings,
  RefreshCw } from
'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { winstonAiService } from '@/services/winstonAiService';
import { motion } from 'motion/react';

interface AnalysisResult {
  plagiarismScore: number;
  aiScore: number;
  originalityScore: number;
  details: string;
  flaggedSections: Array<{
    text: string;
    reason: string;
    confidence: number;
  }>;
  sources?: Array<{
    url: string;
    title: string;
    similarity: number;
  }>;
}

const PlagiarismChecker: React.FC = () => {
  const [textToAnalyze, setTextToAnalyze] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [serviceStatus, setServiceStatus] = useState<{
    status: 'connected' | 'disconnected' | 'error' | 'checking';
    message: string;
    details?: any;
  }>({ status: 'checking', message: 'Checking service status...' });
  const { toast } = useToast();

  // Check service status on component mount
  useEffect(() => {
    checkServiceStatus();
  }, []);

  const checkServiceStatus = async () => {
    setServiceStatus({ status: 'checking', message: 'Checking Winston AI service...' });
    try {
      const status = await winstonAiService.getServiceStatus();
      setServiceStatus(status);
    } catch (error) {
      setServiceStatus({
        status: 'error',
        message: 'Failed to check service status',
        details: error
      });
    }
  };

  const analyzeText = async () => {
    if (!textToAnalyze.trim()) {
      toast({
        title: 'No Text Provided',
        description: 'Please enter some text to analyze.',
        variant: 'destructive'
      });
      return;
    }

    if (textToAnalyze.length < 50) {
      toast({
        title: 'Text Too Short',
        description: 'Please provide at least 50 characters for accurate analysis.',
        variant: 'destructive'
      });
      return;
    }

    setIsAnalyzing(true);
    try {
      console.log('Starting Winston AI plagiarism analysis...');

      const result = await winstonAiService.checkPlagiarism(textToAnalyze);

      if (result.error) {
        throw new Error(result.error);
      }

      if (result.data) {
        setAnalysisResult(result.data);
        toast({
          title: 'Analysis Complete',
          description: 'Winston AI analysis completed successfully.'
        });
      }
    } catch (error) {
      console.error('Error analyzing text:', error);

      // Provide specific error handling for common issues
      let errorMessage = error instanceof Error ? error.message : 'Failed to analyze text. Please try again.';
      let errorTitle = 'Analysis Failed';

      if (errorMessage.includes('Network connection issue') || errorMessage.includes('Failed to fetch')) {
        errorTitle = 'Connection Error';
        errorMessage = 'Unable to connect to Winston AI service. This may be due to network restrictions or API configuration issues. Please contact your administrator.';
      } else if (errorMessage.includes('API key')) {
        errorTitle = 'Configuration Error';
        errorMessage = 'Winston AI API key is not properly configured. Please check the system settings.';
      }

      toast({
        title: errorTitle,
        description: errorMessage,
        variant: 'destructive'
      });

      // Refresh service status after error
      checkServiceStatus();
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {// 5MB limit
        toast({
          title: 'File Too Large',
          description: 'Please select a file smaller than 5MB.',
          variant: 'destructive'
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setTextToAnalyze(content);
        toast({
          title: 'File Uploaded',
          description: `Successfully loaded ${file.name}`
        });
      };
      reader.onerror = () => {
        toast({
          title: 'Upload Failed',
          description: 'Failed to read the file. Please try again.',
          variant: 'destructive'
        });
      };
      reader.readAsText(file);
    }
  };

  const downloadReport = () => {
    if (!analysisResult) return;

    const report = `
WINSTON AI PLAGIARISM & AI DETECTION REPORT
==========================================

Analysis Summary:
- Plagiarism Risk: ${analysisResult.plagiarismScore}%
- AI Generation Likelihood: ${analysisResult.aiScore}%
- Originality Score: ${analysisResult.originalityScore}%

Detailed Analysis:
${analysisResult.details}

Flagged Sections:
${analysisResult.flaggedSections.map((section, index) =>
    `${index + 1}. "${section.text}"
   Reason: ${section.reason}
   Confidence: ${section.confidence}%`
    ).join('\n\n')}

${analysisResult.sources && analysisResult.sources.length > 0 ?
    `\nPotential Sources Found:
${analysisResult.sources.map((source, index) =>
    `${index + 1}. ${source.title}
   URL: ${source.url}
   Similarity: ${Math.round(source.similarity * 100)}%`
    ).join('\n\n')}` : ''}

Report generated on: ${new Date().toLocaleString()}
Powered by Winston AI v3.0
`;

    const element = document.createElement('a');
    const file = new Blob([report], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `winston-ai-report-${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);

    toast({
      title: 'Report Downloaded',
      description: 'Winston AI analysis report has been downloaded.'
    });
  };

  const getScoreColor = (score: number, inverse = false) => {
    if (inverse) {
      if (score >= 80) return 'text-green-600';
      if (score >= 60) return 'text-yellow-600';
      return 'text-red-600';
    } else {
      if (score >= 70) return 'text-red-600';
      if (score >= 40) return 'text-yellow-600';
      return 'text-green-600';
    }
  };

  const getScoreBadge = (score: number, inverse = false) => {
    if (inverse) {
      if (score >= 80) return { variant: 'default' as const, text: 'Excellent' };
      if (score >= 60) return { variant: 'secondary' as const, text: 'Good' };
      return { variant: 'destructive' as const, text: 'Poor' };
    } else {
      if (score >= 70) return { variant: 'destructive' as const, text: 'High Risk' };
      if (score >= 40) return { variant: 'secondary' as const, text: 'Medium Risk' };
      return { variant: 'default' as const, text: 'Low Risk' };
    }
  };

  const getStatusIcon = () => {
    switch (serviceStatus.status) {
      case 'connected':
        return <CheckCircle className="h-4 w-4 text-green-600" data-id="3dbqxcubu" data-path="src/components/ai/PlagiarismChecker.tsx" />;
      case 'disconnected':
        return <WifiOff className="h-4 w-4 text-red-600" data-id="q8iswwwxq" data-path="src/components/ai/PlagiarismChecker.tsx" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" data-id="pzp8ofkke" data-path="src/components/ai/PlagiarismChecker.tsx" />;
      case 'checking':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-600" data-id="g146h6q7t" data-path="src/components/ai/PlagiarismChecker.tsx" />;
      default:
        return <Info className="h-4 w-4 text-gray-600" data-id="0zpi5nmeh" data-path="src/components/ai/PlagiarismChecker.tsx" />;
    }
  };

  const getStatusVariant = () => {
    switch (serviceStatus.status) {
      case 'connected':
        return 'default';
      case 'disconnected':
      case 'error':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="space-y-6" data-id="cdp1q0ufb" data-path="src/components/ai/PlagiarismChecker.tsx">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between" data-id="rx8g3lm2d" data-path="src/components/ai/PlagiarismChecker.tsx">

        <div data-id="rmf28nwfk" data-path="src/components/ai/PlagiarismChecker.tsx">
          <h1 className="text-2xl font-bold text-gray-900" data-id="ru0q8j44q" data-path="src/components/ai/PlagiarismChecker.tsx">Winston AI Plagiarism Detector</h1>
          <p className="text-gray-600" data-id="i1wihefw0" data-path="src/components/ai/PlagiarismChecker.tsx">Advanced plagiarism and AI content detection</p>
        </div>
        <div className="flex items-center space-x-2" data-id="80on2pya9" data-path="src/components/ai/PlagiarismChecker.tsx">
          <Badge variant="secondary" className="bg-blue-100 text-blue-700" data-id="fldf558ko" data-path="src/components/ai/PlagiarismChecker.tsx">
            <Shield className="h-3 w-3 mr-1" data-id="sqqv1ezy5" data-path="src/components/ai/PlagiarismChecker.tsx" />
            Powered by Winston AI
          </Badge>
          <Button
            size="sm"
            variant="outline"
            onClick={checkServiceStatus}
            disabled={serviceStatus.status === 'checking'} data-id="x0nrr6hbz" data-path="src/components/ai/PlagiarismChecker.tsx">

            <RefreshCw className={`h-4 w-4 mr-1 ${serviceStatus.status === 'checking' ? 'animate-spin' : ''}`} data-id="z0qu8q0dx" data-path="src/components/ai/PlagiarismChecker.tsx" />
            Check Status
          </Button>
        </div>
      </motion.div>

      {/* Service Status Alert */}
      <Alert variant={getStatusVariant()} data-id="29vy3lw8x" data-path="src/components/ai/PlagiarismChecker.tsx">
        {getStatusIcon()}
        <AlertDescription className="flex items-center justify-between" data-id="fwyb7xw43" data-path="src/components/ai/PlagiarismChecker.tsx">
          <div data-id="8rpzz33et" data-path="src/components/ai/PlagiarismChecker.tsx">
            <strong data-id="hrm66lar9" data-path="src/components/ai/PlagiarismChecker.tsx">Service Status:</strong> {serviceStatus.message}
            {serviceStatus.details &&
            <details className="mt-2" data-id="87x4g7t85" data-path="src/components/ai/PlagiarismChecker.tsx">
                <summary className="cursor-pointer text-sm" data-id="42fdwmq0i" data-path="src/components/ai/PlagiarismChecker.tsx">View Details</summary>
                <pre className="text-xs mt-1 whitespace-pre-wrap" data-id="1vtfkcje3" data-path="src/components/ai/PlagiarismChecker.tsx">{JSON.stringify(serviceStatus.details, null, 2)}</pre>
              </details>
            }
          </div>
          {serviceStatus.status === 'error' &&
          <Button size="sm" variant="outline" onClick={() => window.location.href = '/settings'} data-id="syfw4zgfk" data-path="src/components/ai/PlagiarismChecker.tsx">
              <Settings className="h-4 w-4 mr-1" data-id="6mxunih6q" data-path="src/components/ai/PlagiarismChecker.tsx" />
              Settings
            </Button>
          }
        </AlertDescription>
      </Alert>

      {/* Info Alert */}
      {serviceStatus.status === 'connected' &&
      <Alert data-id="tlpat0b48" data-path="src/components/ai/PlagiarismChecker.tsx">
          <Info className="h-4 w-4" data-id="0oum4anrw" data-path="src/components/ai/PlagiarismChecker.tsx" />
          <AlertDescription data-id="pbdhuxul6" data-path="src/components/ai/PlagiarismChecker.tsx">
            Winston AI service is ready! Ensure your Winston AI API key is configured in the admin settings for optimal results.
          </AlertDescription>
        </Alert>
      }

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="xaewmlwgy" data-path="src/components/ai/PlagiarismChecker.tsx">
        {/* Input Section */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }} data-id="3xxxc215d" data-path="src/components/ai/PlagiarismChecker.tsx">

          <Card data-id="44a4xll5n" data-path="src/components/ai/PlagiarismChecker.tsx">
            <CardHeader data-id="g5ylcxjfx" data-path="src/components/ai/PlagiarismChecker.tsx">
              <CardTitle className="flex items-center" data-id="0z8d6kggr" data-path="src/components/ai/PlagiarismChecker.tsx">
                <Shield className="h-5 w-5 mr-2" data-id="eah61eus6" data-path="src/components/ai/PlagiarismChecker.tsx" />
                Text Analysis
              </CardTitle>
              <CardDescription data-id="trws6htt2" data-path="src/components/ai/PlagiarismChecker.tsx">
                Upload a file or paste text to check for plagiarism and AI-generated content
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4" data-id="s69dveixd" data-path="src/components/ai/PlagiarismChecker.tsx">
              <Tabs defaultValue="text" className="w-full" data-id="bvyofevnl" data-path="src/components/ai/PlagiarismChecker.tsx">
                <TabsList className="grid w-full grid-cols-2" data-id="2akqtatk1" data-path="src/components/ai/PlagiarismChecker.tsx">
                  <TabsTrigger value="text" data-id="2ocj3pw8t" data-path="src/components/ai/PlagiarismChecker.tsx">Paste Text</TabsTrigger>
                  <TabsTrigger value="upload" data-id="y8nmlgz58" data-path="src/components/ai/PlagiarismChecker.tsx">Upload File</TabsTrigger>
                </TabsList>
                
                <TabsContent value="text" className="space-y-4" data-id="a85b2a6xt" data-path="src/components/ai/PlagiarismChecker.tsx">
                  <div data-id="sttnibuyy" data-path="src/components/ai/PlagiarismChecker.tsx">
                    <Label htmlFor="text-input" data-id="lgkwbpzxo" data-path="src/components/ai/PlagiarismChecker.tsx">Text to Analyze</Label>
                    <Textarea
                      id="text-input"
                      placeholder="Paste the text you want to analyze for plagiarism and AI content..."
                      value={textToAnalyze}
                      onChange={(e) => setTextToAnalyze(e.target.value)}
                      rows={12}
                      className="resize-none" data-id="r29vub4jj" data-path="src/components/ai/PlagiarismChecker.tsx" />

                    <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="4jx2p5q21" data-path="src/components/ai/PlagiarismChecker.tsx">
                      <span data-id="9zwt0tue5" data-path="src/components/ai/PlagiarismChecker.tsx">Minimum 50 characters required</span>
                      <span data-id="35k1oe7fi" data-path="src/components/ai/PlagiarismChecker.tsx">{textToAnalyze.length} characters</span>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="upload" className="space-y-4" data-id="16zehzi1d" data-path="src/components/ai/PlagiarismChecker.tsx">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors" data-id="5pihg3xig" data-path="src/components/ai/PlagiarismChecker.tsx">
                    <input
                      type="file"
                      accept=".txt,.doc,.docx,.pdf"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload" data-id="w3p42i8k7" data-path="src/components/ai/PlagiarismChecker.tsx" />

                    <label htmlFor="file-upload" className="cursor-pointer" data-id="lppfws7te" data-path="src/components/ai/PlagiarismChecker.tsx">
                      <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" data-id="gcraeuklz" data-path="src/components/ai/PlagiarismChecker.tsx" />
                      <p className="text-sm text-gray-600 mb-2" data-id="cuy18ps1v" data-path="src/components/ai/PlagiarismChecker.tsx">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-gray-400" data-id="47cio31pn" data-path="src/components/ai/PlagiarismChecker.tsx">
                        Supports .txt, .doc, .docx, .pdf files (max 5MB)
                      </p>
                    </label>
                  </div>
                  {textToAnalyze &&
                  <Alert data-id="dhbu8lg45" data-path="src/components/ai/PlagiarismChecker.tsx">
                      <FileText className="h-4 w-4" data-id="715g4jihc" data-path="src/components/ai/PlagiarismChecker.tsx" />
                      <AlertDescription data-id="wgwu52jaw" data-path="src/components/ai/PlagiarismChecker.tsx">
                        File content loaded successfully. {textToAnalyze.length} characters detected.
                      </AlertDescription>
                    </Alert>
                  }
                </TabsContent>
              </Tabs>

              <Button
                onClick={analyzeText}
                disabled={isAnalyzing || !textToAnalyze.trim() || serviceStatus.status !== 'connected'}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700" data-id="0l6t4w4iv" data-path="src/components/ai/PlagiarismChecker.tsx">

                {isAnalyzing ?
                <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="7uxqjkret" data-path="src/components/ai/PlagiarismChecker.tsx" />
                    Analyzing with Winston AI...
                  </> :

                <>
                    <Search className="mr-2 h-4 w-4" data-id="dx2eabjsv" data-path="src/components/ai/PlagiarismChecker.tsx" />
                    Analyze with Winston AI
                  </>
                }
              </Button>

              {serviceStatus.status !== 'connected' &&
              <p className="text-xs text-gray-500 text-center" data-id="lhqcjoi2a" data-path="src/components/ai/PlagiarismChecker.tsx">
                  Analysis requires active Winston AI service connection
                </p>
              }
            </CardContent>
          </Card>
        </motion.div>

        {/* Results Section */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }} data-id="lumtird12" data-path="src/components/ai/PlagiarismChecker.tsx">

          <Card className="h-full" data-id="h2mnptkbg" data-path="src/components/ai/PlagiarismChecker.tsx">
            <CardHeader data-id="xup70tjq8" data-path="src/components/ai/PlagiarismChecker.tsx">
              <div className="flex items-center justify-between" data-id="0x4ej02c6" data-path="src/components/ai/PlagiarismChecker.tsx">
                <div data-id="etkn2aoea" data-path="src/components/ai/PlagiarismChecker.tsx">
                  <CardTitle data-id="2fa4hzov8" data-path="src/components/ai/PlagiarismChecker.tsx">Analysis Results</CardTitle>
                  <CardDescription data-id="93zae0axl" data-path="src/components/ai/PlagiarismChecker.tsx">
                    {analysisResult ? 'Winston AI detailed analysis report' : 'Results will appear here'}
                  </CardDescription>
                </div>
                {analysisResult &&
                <Button size="sm" variant="outline" onClick={downloadReport} data-id="d6h2rfvfz" data-path="src/components/ai/PlagiarismChecker.tsx">
                    <Download className="h-4 w-4 mr-1" data-id="b6hxeo6t6" data-path="src/components/ai/PlagiarismChecker.tsx" />
                    Download Report
                  </Button>
                }
              </div>
            </CardHeader>
            <CardContent data-id="tdg9uygbg" data-path="src/components/ai/PlagiarismChecker.tsx">
              {analysisResult ?
              <div className="space-y-6" data-id="kctk746y2" data-path="src/components/ai/PlagiarismChecker.tsx">
                  {/* Score Overview */}
                  <div className="grid grid-cols-1 gap-4" data-id="o1zw8i82n" data-path="src/components/ai/PlagiarismChecker.tsx">
                    <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg" data-id="ppw6wzckt" data-path="src/components/ai/PlagiarismChecker.tsx">
                      <div className="flex items-center justify-between mb-2" data-id="bie8zrspj" data-path="src/components/ai/PlagiarismChecker.tsx">
                        <span className="text-sm font-medium" data-id="34cwmj85l" data-path="src/components/ai/PlagiarismChecker.tsx">Plagiarism Risk</span>
                        <Badge {...getScoreBadge(analysisResult.plagiarismScore)} data-id="csjg09gdq" data-path="src/components/ai/PlagiarismChecker.tsx">
                          {getScoreBadge(analysisResult.plagiarismScore).text}
                        </Badge>
                      </div>
                      <div className="flex items-center space-x-2" data-id="pmixh7tm8" data-path="src/components/ai/PlagiarismChecker.tsx">
                        <Progress value={analysisResult.plagiarismScore} className="flex-1" data-id="4ky22dgsc" data-path="src/components/ai/PlagiarismChecker.tsx" />
                        <span className={`text-lg font-bold ${getScoreColor(analysisResult.plagiarismScore)}`} data-id="y0afgjztz" data-path="src/components/ai/PlagiarismChecker.tsx">
                          {analysisResult.plagiarismScore}%
                        </span>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg" data-id="qdhs6opn7" data-path="src/components/ai/PlagiarismChecker.tsx">
                      <div className="flex items-center justify-between mb-2" data-id="63qnh5rbn" data-path="src/components/ai/PlagiarismChecker.tsx">
                        <span className="text-sm font-medium flex items-center" data-id="64qj7cfi7" data-path="src/components/ai/PlagiarismChecker.tsx">
                          <Brain className="h-4 w-4 mr-1" data-id="rtirwmtew" data-path="src/components/ai/PlagiarismChecker.tsx" />
                          AI Generation Likelihood
                        </span>
                        <Badge {...getScoreBadge(analysisResult.aiScore)} data-id="ldyqddxj2" data-path="src/components/ai/PlagiarismChecker.tsx">
                          {getScoreBadge(analysisResult.aiScore).text}
                        </Badge>
                      </div>
                      <div className="flex items-center space-x-2" data-id="23s4fps2d" data-path="src/components/ai/PlagiarismChecker.tsx">
                        <Progress value={analysisResult.aiScore} className="flex-1" data-id="beuban24k" data-path="src/components/ai/PlagiarismChecker.tsx" />
                        <span className={`text-lg font-bold ${getScoreColor(analysisResult.aiScore)}`} data-id="2z9uv3dyc" data-path="src/components/ai/PlagiarismChecker.tsx">
                          {analysisResult.aiScore}%
                        </span>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg" data-id="vovdknamy" data-path="src/components/ai/PlagiarismChecker.tsx">
                      <div className="flex items-center justify-between mb-2" data-id="zt6og405z" data-path="src/components/ai/PlagiarismChecker.tsx">
                        <span className="text-sm font-medium flex items-center" data-id="sc9zxpo27" data-path="src/components/ai/PlagiarismChecker.tsx">
                          <CheckCircle className="h-4 w-4 mr-1" data-id="3go89ws2n" data-path="src/components/ai/PlagiarismChecker.tsx" />
                          Originality Score
                        </span>
                        <Badge {...getScoreBadge(analysisResult.originalityScore, true)} data-id="p1qndkef9" data-path="src/components/ai/PlagiarismChecker.tsx">
                          {getScoreBadge(analysisResult.originalityScore, true).text}
                        </Badge>
                      </div>
                      <div className="flex items-center space-x-2" data-id="6au3vm25j" data-path="src/components/ai/PlagiarismChecker.tsx">
                        <Progress value={analysisResult.originalityScore} className="flex-1" data-id="g65rjlplv" data-path="src/components/ai/PlagiarismChecker.tsx" />
                        <span className={`text-lg font-bold ${getScoreColor(analysisResult.originalityScore, true)}`} data-id="mnyu4eown" data-path="src/components/ai/PlagiarismChecker.tsx">
                          {analysisResult.originalityScore}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Detailed Report */}
                  <div data-id="5fjfm8rpa" data-path="src/components/ai/PlagiarismChecker.tsx">
                    <h3 className="font-semibold mb-2" data-id="kofvsbrb9" data-path="src/components/ai/PlagiarismChecker.tsx">Winston AI Analysis Report</h3>
                    <div className="bg-gray-50 rounded-lg p-4 max-h-40 overflow-y-auto" data-id="qkko24avf" data-path="src/components/ai/PlagiarismChecker.tsx">
                      <pre className="whitespace-pre-wrap text-sm" data-id="xvkpgmqw5" data-path="src/components/ai/PlagiarismChecker.tsx">{analysisResult.details}</pre>
                    </div>
                  </div>

                  {/* Sources */}
                  {analysisResult.sources && analysisResult.sources.length > 0 &&
                <div data-id="vhq443xvi" data-path="src/components/ai/PlagiarismChecker.tsx">
                      <h3 className="font-semibold mb-2 flex items-center" data-id="jz3fg4dom" data-path="src/components/ai/PlagiarismChecker.tsx">
                        <ExternalLink className="h-4 w-4 mr-1 text-blue-600" data-id="1h09ycssm" data-path="src/components/ai/PlagiarismChecker.tsx" />
                        Potential Sources ({analysisResult.sources.length})
                      </h3>
                      <div className="space-y-2 max-h-32 overflow-y-auto" data-id="cfxdbhzo6" data-path="src/components/ai/PlagiarismChecker.tsx">
                        {analysisResult.sources.map((source, index) =>
                    <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="ysvghnqkk" data-path="src/components/ai/PlagiarismChecker.tsx">
                            <div className="flex items-center justify-between" data-id="btjdm8bpp" data-path="src/components/ai/PlagiarismChecker.tsx">
                              <div className="flex-1" data-id="if4eqbks5" data-path="src/components/ai/PlagiarismChecker.tsx">
                                <p className="text-sm font-medium text-gray-900 truncate" data-id="mwnnbcc0r" data-path="src/components/ai/PlagiarismChecker.tsx">
                                  {source.title}
                                </p>
                                <p className="text-xs text-blue-600 truncate" data-id="jjxrhzjuq" data-path="src/components/ai/PlagiarismChecker.tsx">
                                  {source.url}
                                </p>
                              </div>
                              <Badge variant="outline" className="text-xs ml-2" data-id="wm0g8xzfr" data-path="src/components/ai/PlagiarismChecker.tsx">
                                {Math.round(source.similarity * 100)}% similarity
                              </Badge>
                            </div>
                          </div>
                    )}
                      </div>
                    </div>
                }

                  {/* Flagged Sections */}
                  {analysisResult.flaggedSections.length > 0 &&
                <div data-id="w846vs195" data-path="src/components/ai/PlagiarismChecker.tsx">
                      <h3 className="font-semibold mb-2 flex items-center" data-id="211xzti4p" data-path="src/components/ai/PlagiarismChecker.tsx">
                        <AlertTriangle className="h-4 w-4 mr-1 text-yellow-600" data-id="txhz26zft" data-path="src/components/ai/PlagiarismChecker.tsx" />
                        Flagged Sections ({analysisResult.flaggedSections.length})
                      </h3>
                      <div className="space-y-2 max-h-40 overflow-y-auto" data-id="7ifk3348a" data-path="src/components/ai/PlagiarismChecker.tsx">
                        {analysisResult.flaggedSections.map((section, index) =>
                    <div key={index} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3" data-id="s19nwn3zf" data-path="src/components/ai/PlagiarismChecker.tsx">
                            <div className="text-sm font-medium text-gray-900 mb-1" data-id="7u0g6h8bx" data-path="src/components/ai/PlagiarismChecker.tsx">
                              "{section.text}"
                            </div>
                            <div className="flex items-center justify-between text-xs" data-id="pp2l2411s" data-path="src/components/ai/PlagiarismChecker.tsx">
                              <span className="text-gray-600" data-id="z52ah271b" data-path="src/components/ai/PlagiarismChecker.tsx">{section.reason}</span>
                              <Badge variant="outline" className="text-xs" data-id="pcjlad67u" data-path="src/components/ai/PlagiarismChecker.tsx">
                                {section.confidence}% confidence
                              </Badge>
                            </div>
                          </div>
                    )}
                      </div>
                    </div>
                }
                </div> :

              <div className="flex items-center justify-center h-96 text-gray-500" data-id="hs4bx7l7v" data-path="src/components/ai/PlagiarismChecker.tsx">
                  <div className="text-center" data-id="6bxnrckf9" data-path="src/components/ai/PlagiarismChecker.tsx">
                    <Shield className="h-12 w-12 mx-auto mb-4 text-gray-300" data-id="1b7oom3vc" data-path="src/components/ai/PlagiarismChecker.tsx" />
                    <p data-id="whsb9ge8j" data-path="src/components/ai/PlagiarismChecker.tsx">Upload or paste text to begin Winston AI analysis</p>
                    <p className="text-sm text-gray-400 mt-2" data-id="u29408iuo" data-path="src/components/ai/PlagiarismChecker.tsx">
                      Professional-grade plagiarism and AI content detection
                    </p>
                    {serviceStatus.status !== 'connected' &&
                  <p className="text-sm text-red-500 mt-2" data-id="l8sonki26" data-path="src/components/ai/PlagiarismChecker.tsx">
                        Service connection required for analysis
                      </p>
                  }
                  </div>
                </div>
              }
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>);

};

export default PlagiarismChecker;